/** Formatting helpers. The API stores and returns money in kobo. */

const ngn = new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 });
const ngn2 = new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 2 });
const int = new Intl.NumberFormat('en-NG');
const compact = new Intl.NumberFormat('en-NG', { notation: 'compact', maximumFractionDigits: 1 });

export const kobo = (v) => Number(v || 0) / 100;

/** ₦12,345 from kobo. Pass { exact: true } for kobo precision. */
export function money(koboValue, { exact = false } = {}) {
  return (exact ? ngn2 : ngn).format(kobo(koboValue)).replace('NGN', '₦');
}

export function moneyCompact(koboValue) {
  const v = kobo(koboValue);
  return v >= 100000 ? `₦${compact.format(v)}` : money(koboValue);
}

export const num = (v) => int.format(Number(v || 0));
export const numCompact = (v) => (Number(v) >= 100000 ? compact.format(Number(v)) : num(v));

/** Percentage change from previous to current; null when there is no base. */
export function change(current, previous) {
  const c = Number(current || 0);
  const p = Number(previous || 0);
  if (!p) return c ? null : 0;
  return ((c - p) / p) * 100;
}

export function pct(v, digits = 1) {
  if (v === null || v === undefined || Number.isNaN(v)) return '–';
  return `${Number(v).toFixed(digits)}%`;
}

/* ---------- Dates (local time, YYYY-MM-DD) ---------- */

export function ymd(d) {
  const x = new Date(d);
  const m = `${x.getMonth() + 1}`.padStart(2, '0');
  const day = `${x.getDate()}`.padStart(2, '0');
  return `${x.getFullYear()}-${m}-${day}`;
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function parseYmd(s) {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function daysBetween(from, to) {
  return Math.round((parseYmd(to) - parseYmd(from)) / 86400000) + 1;
}

const shortDate = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const longDate = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
const dateTime = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

export const fmtShort = (s) => (s ? shortDate.format(parseYmd(s)) : '');
export const fmtDate = (s) => (s ? longDate.format(parseYmd(String(s).slice(0, 10))) : '–');

/** Accepts 'YYYY-MM-DD HH:mm[:ss]' from the API. */
export function fmtDateTime(s) {
  if (!s) return '–';
  const d = new Date(String(s).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? String(s) : dateTime.format(d);
}

export function fmtRange(from, to) {
  if (!from || !to) return '';
  if (from === to) return fmtDate(from);
  const a = parseYmd(from);
  const b = parseYmd(to);
  if (a.getFullYear() === b.getFullYear()) return `${shortDate.format(a)} – ${longDate.format(b)}`;
  return `${longDate.format(a)} – ${longDate.format(b)}`;
}

/* ---------- Telcos ---------- */

export const TELCOS = {
  MTN: { label: 'MTN', color: '#D9A400' },
  AIRTEL: { label: 'Airtel', color: '#D3202F' },
  GLO: { label: 'Glo', color: '#3E9A3A' },
  NINEMOBILE: { label: '9mobile', color: '#006B4F' },
};

export const TELCO_OPTIONS = Object.entries(TELCOS).map(([value, t]) => ({ value, label: t.label }));

export const telcoLabel = (t) => (TELCOS[String(t || '').toUpperCase()] || { label: t || 'Unknown' }).label;
export const telcoColor = (t) => (TELCOS[String(t || '').toUpperCase()] || { color: '#7A8C90' }).color;

/** Nigerian numbers are stored as 234XXXXXXXXXX; show them the local way. */
export function phone(p) {
  const s = String(p || '');
  if (/^234\d{10}$/.test(s)) return `0${s.slice(3, 6)} ${s.slice(6, 9)} ${s.slice(9)}`;
  return s || '–';
}
